import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { clock, dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Table } from './Reports.tsx';

/**
 * IoT: the business's sensors at a glance – the value now, the last day as a small chart and
 * whether it is in its safe range – their alerts, and the connectors that send the readings.
 */

const KINDS = ['temperature', 'humidity', 'energy', 'power', 'door', 'weight', 'co2', 'counter', 'other'] as const;
type Kind = (typeof KINDS)[number];
const UNITS: Record<Kind, string> = { temperature: '°C', humidity: '%', energy: 'kWh', power: 'W', door: '', weight: 'kg', co2: 'ppm', counter: '', other: '' };
const KIND_ICON: Record<Kind, IconName> = {
  temperature: 'sun',
  humidity: 'circle',
  energy: 'flame',
  power: 'flame',
  door: 'key',
  weight: 'box',
  co2: 'globe',
  counter: 'hash',
  other: 'cpu',
};
type DeviceState = 'ok' | 'high' | 'low' | 'offline' | 'waiting';
const STATE: Record<DeviceState, { tone: Tone; icon: IconName }> = {
  ok: { tone: 'ok', icon: 'check' },
  high: { tone: 'danger', icon: 'up' },
  low: { tone: 'danger', icon: 'down' },
  offline: { tone: 'warn', icon: 'alert' },
  waiting: { tone: 'neutral', icon: 'clock' },
};

interface Device {
  id: string;
  code: string;
  name: string;
  kind: Kind;
  unit: string | null;
  location: string | null;
  connector: string | null;
  connector_name: string | null;
  min: number | null;
  max: number | null;
  delay_minutes: number;
  offline_minutes: number;
  active: boolean;
  state: DeviceState;
  last_value: number | null;
  last_at: string | null;
  alerts?: number;
  spark?: (number | null)[];
}
interface Overview {
  now: string;
  devices: Device[];
  alerts: Partial<Record<'open' | 'acknowledged', number>>;
  connectors: number;
}
interface AlertRow {
  id: string;
  device: string;
  device_name?: string;
  unit?: string | null;
  location?: string | null;
  kind: 'high' | 'low' | 'offline';
  state: 'open' | 'acknowledged' | 'resolved';
  started_at: string;
  resolved_at: string | null;
  peak: number | null;
  acknowledged_name: string | null;
  acknowledged_at: string | null;
  note: string | null;
}
interface Detail {
  now: string;
  device: Device;
  points: { t: string; v: number; lo: number; hi: number }[];
  stats: { count: number; min: number | null; max: number | null; avg: number | null; inside: number | null };
  recent: { id: string; at: string; value: number; by_name: string | null }[];
  alerts: AlertRow[];
}

// ---- formatting -----------------------------------------------------------------------------

/** A value in thousandths as people read it: "3.2 °C", "Open". */
export function valueText(v: number | null | undefined, d: { kind: Kind; unit: string | null }): string {
  if (v === null || v === undefined) return '—';
  if (d.kind === 'door') return v >= 1000 ? t('iot.door_open') : t('iot.door_closed');
  const n = v / 1000;
  const abs = Math.abs(n);
  const text = new Intl.NumberFormat(locale(), { maximumFractionDigits: abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 10 ? 1 : 2 }).format(n);
  return d.unit ? `${text} ${d.unit}` : text;
}
const rangeText = (d: Device) =>
  d.kind === 'door'
    ? d.max === 0
      ? t('iot.range_closed')
      : t('iot.range_none')
    : d.min !== null && d.max !== null
      ? t('iot.range_between', { min: valueText(d.min, d), max: valueText(d.max, d) })
      : d.max !== null
        ? t('iot.range_max', { max: valueText(d.max, d) })
        : d.min !== null
          ? t('iot.range_min', { min: valueText(d.min, d) })
          : t('iot.range_none');
/** "21.5" or "-18" → thousandths; null when it is not a number. */
function parseValue(text: string): number | null {
  const clean = text.replace(/[\s ]/g, '').replace(',', '.').replace('−', '-');
  if (!/^-?\d+(\.\d{1,3})?$/.test(clean)) return null;
  const n = Math.round(Number(clean) * 1000);
  return Number.isSafeInteger(n) ? n : null;
}
const editText = (v: number | null) => (v === null ? '' : String(v / 1000));
const stateLabel = (s: DeviceState) => t(`iot.st_${s}` as MessageKey);

function Spark({ values, min, max, state }: { values: (number | null)[]; min: number | null; max: number | null; state: DeviceState }) {
  const pts = values.map((v, i) => (v === null ? null : { i, v })).filter((p): p is { i: number; v: number } => !!p);
  if (pts.length < 2) return <div className="iot-spark empty" aria-hidden="true" />;
  const all = [...pts.map((p) => p.v), ...(min !== null ? [min] : []), ...(max !== null ? [max] : [])];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = hi - lo || 1;
  const x = (i: number) => (i / 23) * 100;
  const y = (v: number) => 36 - ((v - lo) / span) * 32;
  const d = pts.map((p, n) => `${n ? 'L' : 'M'}${x(p.i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  return (
    <svg className={`iot-spark iot-line-${state}`} viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      {max !== null ? <line className="iot-limit" x1="0" x2="100" y1={y(max)} y2={y(max)} /> : null}
      {min !== null ? <line className="iot-limit" x1="0" x2="100" y1={y(min)} y2={y(min)} /> : null}
      <path d={d} />
    </svg>
  );
}

// ---- the page -------------------------------------------------------------------------------

type Tab = 'devices' | 'alerts' | 'connectors';

export function Iot() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const manage = can('iot.manage');
  const { data: o, reload } = useAction<Overview>('iot.overview');
  useEffect(() => subscribe(['iot'], () => void reload()), [reload]);
  const tabs: { key: Tab; icon: IconName; show: boolean; badge?: number }[] = [
    { key: 'devices', icon: 'cpu', show: true },
    { key: 'alerts', icon: 'bell', show: true, badge: o?.alerts.open },
    { key: 'connectors', icon: 'link', show: manage },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'devices') as Tab;
  const setTab = (k: Tab) => setParams(k === 'devices' ? {} : { tab: k }, { replace: true });
  const [add, setAdd] = useState(false);
  const counts = useMemo(() => {
    const c = { ok: 0, out: 0, offline: 0, waiting: 0 };
    for (const d of o?.devices ?? []) {
      if (!d.active) {
        if (d.state === 'waiting') c.waiting++;
        continue;
      }
      if (d.state === 'ok') c.ok++;
      else if (d.state === 'high' || d.state === 'low') c.out++;
      else if (d.state === 'offline') c.offline++;
    }
    return c;
  }, [o]);
  return (
    <Shell>
      <div className="page reports iot-page">
        <div className="page-head">
          <div>
            <h1>{t('iot.title')}</h1>
            <p className="muted">{o ? (counts.out || counts.offline ? [counts.out ? tn('iot.sum_out', counts.out) : '', counts.offline ? tn('iot.sum_offline', counts.offline) : ''].filter(Boolean).join(' · ') : t('iot.lead_ok')) : ' '}</p>
          </div>
          {manage && tab === 'devices' ? (
            <Button variant="primary" icon="plus" onClick={() => setAdd(true)}>
              {t('iot.add_device')}
            </Button>
          ) : null}
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('iot.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`iot.tab_${x.key}` as MessageKey)}
              {x.badge ? <span className="iot-tab-badge">{x.badge}</span> : null}
            </button>
          ))}
        </div>
        {!o ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : tab === 'devices' ? (
          <DevicesTab o={o} manage={manage} counts={counts} onChange={reload} onConnectors={() => setTab('connectors')} />
        ) : tab === 'alerts' ? (
          <AlertsTab onChange={reload} />
        ) : (
          <ConnectorsTab onChange={reload} />
        )}
        {add ? <DeviceDialog device={null} onClose={() => setAdd(false)} onSaved={reload} /> : null}
      </div>
    </Shell>
  );
}

function DevicesTab({
  o,
  manage,
  counts,
  onChange,
  onConnectors,
}: {
  o: Overview;
  manage: boolean;
  counts: { ok: number; out: number; offline: number; waiting: number };
  onChange: () => Promise<unknown>;
  onConnectors: () => void;
}) {
  const navigate = useNavigate();
  const [edit, setEdit] = useState<Device | null>(null);
  const active = o.devices.filter((d) => d.active);
  const waiting = o.devices.filter((d) => !d.active && d.state === 'waiting');
  const off = o.devices.filter((d) => !d.active && d.state !== 'waiting');
  // Problems first, then by place, so what needs a look is at the top.
  const rank: Record<DeviceState, number> = { high: 0, low: 0, offline: 1, ok: 2, waiting: 3 };
  const tiles = [...active].sort((a, b) => rank[a.state] - rank[b.state] || (a.location ?? '').localeCompare(b.location ?? '') || a.name.localeCompare(b.name));
  if (!o.devices.length)
    return (
      <section className="panel">
        <EmptyState
          icon="cpu"
          title={t('iot.empty_title')}
          body={t('iot.empty_body')}
          action={manage ? <Button icon="link" onClick={onConnectors}>{t('iot.set_up_connector')}</Button> : undefined}
        />
      </section>
    );
  return (
    <>
      <div className="iot-summary" role="status">
        <span className="iot-sum ok">
          <Icon name="check" size={16} />
          {tn('iot.sum_ok', counts.ok)}
        </span>
        {counts.out ? (
          <span className="iot-sum danger">
            <Icon name="alert" size={16} />
            {tn('iot.sum_out', counts.out)}
          </span>
        ) : null}
        {counts.offline ? (
          <span className="iot-sum warn">
            <Icon name="alert" size={16} />
            {tn('iot.sum_offline', counts.offline)}
          </span>
        ) : null}
      </div>
      {manage && waiting.length ? (
        <div className="callout callout-info iot-waiting">
          <Icon name="info" size={18} />
          <div>
            <p>{tn('iot.waiting', waiting.length)}</p>
            <div className="iot-row-actions">
              {waiting.map((d) => (
                <Button key={d.id} size="sm" icon="check" onClick={() => setEdit(d)}>
                  {t('iot.turn_on', { code: d.code })}
                </Button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <div className="iot-tiles">
        {tiles.map((d) => (
          <button key={d.id} type="button" className={`iot-tile iot-tile-${d.state}`} onClick={() => navigate(`/iot/${d.id}`)}>
            <span className="iot-tile-head">
              <span className="iot-kind" aria-hidden="true">
                <Icon name={KIND_ICON[d.kind]} size={16} />
              </span>
              <span className="iot-tile-name">
                {d.name}
                <span className="iot-tile-place">{d.location || t('iot.no_location')}</span>
              </span>
              {d.alerts ? <span className="iot-tile-alerts">{tn('iot.alerts_n', d.alerts)}</span> : null}
            </span>
            <span className="iot-value">{valueText(d.last_value, d)}</span>
            <Spark values={d.spark ?? []} min={d.min} max={d.max} state={d.state} />
            <span className="iot-tile-foot">
              <StatusBadge tone={STATE[d.state].tone} icon={STATE[d.state].icon} label={stateLabel(d.state)} />
              <span className="muted small">{d.last_at ? since(d.last_at, Date.parse(o.now)) : t('iot.never')}</span>
            </span>
            <span className="muted small iot-range">{rangeText(d)}</span>
          </button>
        ))}
      </div>
      {off.length ? (
        <section className="panel">
          <h2 className="iot-h2">{t('iot.turned_off')}</h2>
          <Table
            head={[t('iot.device'), t('iot.code'), t('iot.location'), t('iot.last')]}
            left={[1, 2]}
            phoneHide={[1, 2]}
            rows={off.map((d) => ({ key: d.id, onClick: () => navigate(`/iot/${d.id}`), cells: [d.name, d.code, d.location ?? '—', valueText(d.last_value, d)] }))}
          />
        </section>
      ) : null}
      {edit ? <DeviceDialog device={{ ...edit, active: true }} onClose={() => setEdit(null)} onSaved={onChange} /> : null}
    </>
  );
}

function DeviceDialog({ device, onClose, onSaved }: { device: Device | null; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const navigate = useNavigate();
  const [f, setF] = useState({
    code: device?.code ?? '',
    name: device && device.name !== device.code ? device.name : '',
    kind: device?.kind ?? ('temperature' as Kind),
    unit: device?.unit ?? UNITS[device?.kind ?? 'temperature'],
    location: device?.location ?? '',
    min: editText(device?.min ?? null),
    max: editText(device?.max ?? null),
    delay: String(device?.delay_minutes ?? 10),
    offline: String(device?.offline_minutes ?? 30),
    active: device?.active ?? true,
  });
  const [drop, setDrop] = useState(false);
  const min = f.min.trim() ? parseValue(f.min) : null;
  const max = f.max.trim() ? parseValue(f.max) : null;
  const delay = Number(f.delay);
  const offline = Number(f.offline);
  const badMin = !!f.min.trim() && min === null;
  const badMax = !!f.max.trim() && max === null;
  const badOrder = min !== null && max !== null && min >= max;
  const ok = f.code.trim() && f.name.trim() && !badMin && !badMax && !badOrder && Number.isInteger(delay) && delay >= 0 && Number.isInteger(offline) && offline >= 5;
  const door = f.kind === 'door';
  return (
    <Dialog
      title={device ? t('iot.edit_device') : t('iot.add_device')}
      onClose={onClose}
      footer={
        <>
          {device ? (
            <Button variant="ghost" icon="trash" className="iot-left" onClick={() => setDrop(true)}>
              {t('iot.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                await action('iot.save_device', {
                  id: device?.id ?? null,
                  code: f.code,
                  name: f.name,
                  kind: f.kind,
                  unit: door ? null : f.unit || null,
                  location: f.location || null,
                  min: door ? null : min,
                  max: door ? (f.max === '0' ? 0 : null) : max,
                  delay_minutes: delay,
                  offline_minutes: offline,
                  active: f.active,
                });
                await onSaved();
                onClose();
              }, t('iot.device_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('iot.name')} required>
          <input className="input" value={f.name} maxLength={80} placeholder={t('iot.name_ph')} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('iot.code')} hint={t('iot.code_hint')} required>
          <input className="input iot-mono" value={f.code} maxLength={60} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
        <Field label={t('iot.kind')}>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Kind, unit: UNITS[e.target.value as Kind], max: e.target.value === 'door' ? '' : f.max })}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`iot.kind_${k}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('iot.location')}>
          <input className="input" value={f.location} maxLength={80} placeholder={t('iot.location_ph')} onChange={(e) => setF({ ...f, location: e.target.value })} />
        </Field>
        {!door ? (
          <Field label={t('iot.unit')}>
            <input className="input" value={f.unit} maxLength={10} onChange={(e) => setF({ ...f, unit: e.target.value })} />
          </Field>
        ) : null}
      </div>
      <h3 className="iot-sub">{t('iot.safe_range')}</h3>
      <p className="muted small">{door ? t('iot.door_lead') : t('iot.range_lead')}</p>
      {door ? (
        <label className="check-row">
          <input type="checkbox" checked={f.max === '0'} onChange={(e) => setF({ ...f, max: e.target.checked ? '0' : '' })} />
          <span>{t('iot.door_alert')}</span>
        </label>
      ) : (
        <div className="form-grid">
          <Field label={t('iot.lowest', { unit: f.unit || '—' })} error={badMin ? t('iot.number_bad') : undefined}>
            <input className="input" inputMode="decimal" value={f.min} placeholder={t('iot.no_limit')} onChange={(e) => setF({ ...f, min: e.target.value })} />
          </Field>
          <Field label={t('iot.highest', { unit: f.unit || '—' })} error={badMax ? t('iot.number_bad') : badOrder ? t('iot.order_bad') : undefined}>
            <input className="input" inputMode="decimal" value={f.max} placeholder={t('iot.no_limit')} onChange={(e) => setF({ ...f, max: e.target.value })} />
          </Field>
        </div>
      )}
      <div className="form-grid">
        <Field label={t('iot.delay')} hint={t('iot.delay_hint')}>
          <input className="input" inputMode="numeric" value={f.delay} onChange={(e) => setF({ ...f, delay: e.target.value })} />
        </Field>
        <Field label={t('iot.offline_after')} hint={t('iot.offline_hint')} error={f.offline && offline < 5 ? t('iot.offline_bad') : undefined}>
          <input className="input" inputMode="numeric" value={f.offline} onChange={(e) => setF({ ...f, offline: e.target.value })} />
        </Field>
      </div>
      {device ? (
        <label className="check-row">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
          <span>{t('iot.active')}</span>
        </label>
      ) : null}
      {drop && device ? (
        <Confirm
          title={t('iot.delete_title', { name: device.name })}
          body={t('iot.delete_body')}
          confirm={t('iot.delete')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('iot.delete_device', { id: device.id });
              await onSaved();
              onClose();
              navigate('/iot');
            }, t('iot.device_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- alerts ---------------------------------------------------------------------------------

function AlertsTab({ onChange }: { onChange: () => Promise<unknown> }) {
  const [which, setWhich] = useState<'active' | 'resolved'>('active');
  const { data, reload } = useAction<AlertRow[]>('iot.alerts', { state: which });
  useEffect(() => subscribe(['iot.alert'], () => void reload()), [reload]);
  const [ack, setAck] = useState<AlertRow | null>(null);
  return (
    <section className="panel">
      <div className="iot-panel-head">
        <div className="segmented" role="group" aria-label={t('iot.which_alerts')}>
          {(['active', 'resolved'] as const).map((w) => (
            <button key={w} type="button" aria-pressed={which === w} className={which === w ? 'active' : ''} onClick={() => setWhich(w)}>
              {t(`iot.alerts_${w}` as MessageKey)}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : !data.length ? (
        <EmptyState icon="bell" title={which === 'active' ? t('iot.no_alerts') : t('iot.no_past_alerts')} body={which === 'active' ? t('iot.no_alerts_body') : undefined} />
      ) : (
        <ul className="iot-alerts">
          {data.map((a) => (
            <AlertItem key={a.id} a={a} onAck={() => setAck(a)} />
          ))}
        </ul>
      )}
      {ack ? (
        <AckDialog
          a={ack}
          onClose={() => setAck(null)}
          onDone={async () => {
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

function AlertItem({ a, onAck, device }: { a: AlertRow; onAck?: () => void; device?: Device }) {
  const d = device ?? { kind: 'other' as Kind, unit: a.unit ?? null };
  const tone: Tone = a.state === 'resolved' ? 'neutral' : a.state === 'acknowledged' ? 'info' : a.kind === 'offline' ? 'warn' : 'danger';
  return (
    <li className={`iot-alert iot-alert-${a.state}`}>
      <span className={`iot-alert-icon tone-${tone === 'danger' ? 'red' : tone === 'warn' ? 'orange' : tone === 'info' ? 'blue' : 'grey'}`} aria-hidden="true">
        <Icon name={a.kind === 'offline' ? 'alert' : a.kind === 'high' ? 'up' : 'down'} size={18} />
      </span>
      <div className="iot-alert-body">
        <div className="iot-alert-head">
          <strong>
            {a.device_name ? (
              <Link to={`/iot/${a.device}`}>{a.device_name}</Link>
            ) : null}
            {a.device_name ? ' · ' : ''}
            {t(`iot.ak_${a.kind}` as MessageKey)}
          </strong>
          <StatusBadge tone={tone} icon={a.state === 'resolved' ? 'check' : a.state === 'acknowledged' ? 'eye' : 'bell'} label={t(`iot.as_${a.state}` as MessageKey)} />
        </div>
        <p className="muted small">
          {a.location ? `${a.location} · ` : ''}
          {t('iot.since', { when: dateTime(a.started_at) })}
          {a.resolved_at ? ` · ${t('iot.until', { when: clock(a.resolved_at) })}` : ''}
          {a.peak !== null ? ` · ${t('iot.peak', { value: valueText(a.peak, d) })}` : ''}
        </p>
        {a.acknowledged_name || a.note ? (
          <p className="iot-ack small">
            <Icon name="check" size={14} />
            <span>
              {a.acknowledged_name ? t('iot.ack_by', { name: a.acknowledged_name, when: a.acknowledged_at ? clock(a.acknowledged_at) : '' }) : null}
              {a.note ? ` “${a.note}”` : ''}
            </span>
          </p>
        ) : null}
      </div>
      {onAck && a.state === 'open' ? (
        <Button size="sm" icon="check" onClick={onAck}>
          {t('iot.acknowledge')}
        </Button>
      ) : null}
    </li>
  );
}

function AckDialog({ a, onClose, onDone }: { a: AlertRow; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [note, setNote] = useState('');
  return (
    <Dialog
      title={t('iot.ack_title')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action('iot.acknowledge', { id: a.id, note: note || null });
                await onDone();
                onClose();
              }, t('iot.acked'))
            }
          >
            {t('iot.acknowledge')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('iot.ack_lead')}</p>
        <Field label={t('iot.what_done')}>
          <textarea className="input" rows={3} maxLength={300} value={note} placeholder={t('iot.what_done_ph')} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- connectors -----------------------------------------------------------------------------

interface ConnectorRow {
  id: string;
  name: string;
  active: boolean;
  last_seen_at: string | null;
  version: string | null;
  devices: number;
  waiting: number;
}

function ConnectorsTab({ onChange }: { onChange: () => Promise<unknown> }) {
  const { data, reload } = useAction<{ connectors: ConnectorRow[]; slug: string }>('iot.connectors');
  const { busy, run } = useSave();
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState<{ name: string; key: string } | null>(null);
  const [drop, setDrop] = useState<ConnectorRow | null>(null);
  const [renew, setRenew] = useState<ConnectorRow | null>(null);
  const changed = async () => {
    await Promise.all([reload(), onChange()]);
  };
  return (
    <>
      <section className="panel">
        <div className="iot-panel-head">
          <div>
            <h2 className="iot-h2">{t('iot.connectors')}</h2>
            <p className="muted small">{t('iot.connectors_lead')}</p>
          </div>
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('iot.add_connector')}
          </Button>
        </div>
        {!data ? (
          <Skeleton h={160} />
        ) : !data.connectors.length ? (
          <EmptyState icon="link" title={t('iot.no_connectors')} body={t('iot.no_connectors_body')} />
        ) : (
          <Table
            head={[t('iot.connector'), t('iot.last_heard'), t('iot.devices'), '']}
            left={[1]}
            phoneHide={[2]}
            rows={data.connectors.map((c) => ({
              key: c.id,
              cells: [
                <span className="iot-conn">
                  <strong>{c.name}</strong>
                  {c.version ? <span className="muted small">{c.version}</span> : null}
                </span>,
                c.last_seen_at ? since(c.last_seen_at) : <span className="muted">{t('iot.never')}</span>,
                c.waiting ? `${c.devices} · ${tn('iot.waiting_short', c.waiting)}` : String(c.devices),
                <span className="iot-row-actions">
                  <IconButton icon="key" label={t('iot.new_key_for', { name: c.name })} onClick={() => setRenew(c)} />
                  <IconButton icon="trash" label={t('iot.remove_connector_n', { name: c.name })} onClick={() => setDrop(c)} />
                </span>,
              ],
            }))}
          />
        )}
      </section>
      {data ? <Protocol slug={data.slug} /> : null}
      {adding ? (
        <Dialog
          title={t('iot.add_connector')}
          size="sm"
          onClose={() => setAdding(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                icon="check"
                busy={busy}
                disabled={!name.trim()}
                onClick={() =>
                  void run(async () => {
                    const r = await action<{ name: string; key: string }>('iot.add_connector', { name });
                    setAdding(false);
                    setName('');
                    setKey(r);
                    await changed();
                  })
                }
              >
                {t('iot.make_key')}
              </Button>
            </>
          }
        >
          <Field label={t('iot.connector_name')} hint={t('iot.connector_name_hint')} required>
            <input className="input" value={name} maxLength={60} placeholder={t('iot.connector_name_ph')} onChange={(e) => setName(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {key ? (
        <Dialog
          title={t('iot.key_title', { name: key.name })}
          onClose={() => setKey(null)}
          footer={
            <Button variant="primary" onClick={() => setKey(null)}>
              {t('iot.key_done')}
            </Button>
          }
        >
          <div className="form-stack">
            <p className="callout callout-warn">
              <Icon name="lock" size={18} />
              <span>{t('iot.key_once')}</span>
            </p>
            <div className="iot-key">
              <code>{key.key}</code>
              <Button size="sm" icon="copy" onClick={() => void navigator.clipboard?.writeText(key.key)}>
                {t('iot.copy')}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
      {renew ? (
        <Confirm
          title={t('iot.new_key_title', { name: renew.name })}
          body={t('iot.new_key_body')}
          confirm={t('iot.new_key')}
          onClose={() => setRenew(null)}
          onConfirm={() =>
            void run(async () => {
              const r = await action<{ name: string; key: string }>('iot.new_key', { id: renew.id });
              setRenew(null);
              setKey(r);
              await changed();
            })
          }
        />
      ) : null}
      {drop ? (
        <Confirm
          title={t('iot.remove_connector_title', { name: drop.name })}
          body={t('iot.remove_connector_body')}
          confirm={t('iot.remove_connector')}
          danger
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('iot.remove_connector', { id: drop.id });
              setDrop(null);
              await changed();
            }, t('iot.connector_removed'))
          }
        />
      ) : null}
    </>
  );
}

function Protocol({ slug }: { slug: string }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const example = `curl -X POST ${origin}/api/iot/push \\
  -H "Authorization: Bearer iot.${slug}.…" \\
  -H "Content-Type: application/json" \\
  -d '{"readings":[{"device":"fridge-1","value":3.4},{"device":"door-1","value":1,"at":"2026-10-28T09:15:00Z"}]}'`;
  return (
    <section className="panel">
      <h2 className="iot-h2">{t('iot.protocol')}</h2>
      <p className="muted small">{t('iot.protocol_lead')}</p>
      <pre className="iot-code">{example}</pre>
      <ul className="iot-rules small">
        <li>{t('iot.rule_value')}</li>
        <li>{t('iot.rule_at')}</li>
        <li>{t('iot.rule_new')}</li>
        <li>{t('iot.rule_limit')}</li>
      </ul>
    </section>
  );
}

// ---- one device -----------------------------------------------------------------------------

type Range = '24h' | '7d' | '30d';

export function IotDevice() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const [range, setRange] = useState<Range>('24h');
  const { data, reload } = useAction<Detail>('iot.device', { id, range });
  useEffect(() => subscribe(['iot'], (_e, p) => (!p?.device || p.device === id ? void reload() : undefined)), [reload, id]);
  const [edit, setEdit] = useState(false);
  const [log, setLog] = useState(false);
  const [ack, setAck] = useState<AlertRow | null>(null);
  const d = data?.device;
  return (
    <Shell>
      <div className="page reports iot-page">
        <nav className="crumbs" aria-label={t('iot.crumbs')}>
          <Link to="/iot">{t('iot.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{d?.name ?? '…'}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1 className="iot-title">
              {d?.name ?? ' '}
              {d ? <StatusBadge tone={STATE[d.state].tone} icon={STATE[d.state].icon} label={d.active ? stateLabel(d.state) : t('iot.off')} /> : null}
            </h1>
            <p className="muted">{d ? [t(`iot.kind_${d.kind}` as MessageKey), d.location, d.connector_name ? t('iot.via', { name: d.connector_name }) : t('iot.by_hand')].filter(Boolean).join(' · ') : ' '}</p>
          </div>
          {d ? (
            <span className="iot-row-actions">
              {can('iot.record') && d.active && d.kind !== 'door' ? (
                <Button icon="pen" onClick={() => setLog(true)}>
                  {t('iot.write_reading')}
                </Button>
              ) : null}
              {can('iot.manage') ? (
                <Button icon="edit" onClick={() => setEdit(true)}>
                  {t('iot.edit')}
                </Button>
              ) : null}
            </span>
          ) : null}
        </div>
        {!data || !d ? (
          <Skeleton h={420} r="var(--radius-lg)" />
        ) : (
          <>
            <div className="rep-kpis">
              <div className={`rep-kpi iot-now iot-tile-${d.state}`}>
                <span className="rep-kpi-label">{t('iot.now')}</span>
                <span className="rep-kpi-value">{valueText(d.last_value, d)}</span>
                <span className="rep-kpi-foot muted">{d.last_at ? since(d.last_at, Date.parse(data.now)) : t('iot.never')}</span>
              </div>
              <Stat label={t('iot.lowest_seen')} value={valueText(data.stats.min, d)} />
              <Stat label={t('iot.highest_seen')} value={valueText(data.stats.max, d)} />
              <Stat label={t('iot.in_range')} value={data.stats.inside === null || (d.min === null && d.max === null) ? '—' : `${data.stats.inside}%`} hint={rangeText(d)} />
            </div>
            <section className="panel">
              <div className="iot-panel-head">
                <h2 className="iot-h2">{t('iot.history')}</h2>
                <span className="iot-row-actions">
                  <div className="segmented" role="group" aria-label={t('iot.range')}>
                    {(['24h', '7d', '30d'] as Range[]).map((r) => (
                      <button key={r} type="button" aria-pressed={range === r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>
                        {t(`iot.r_${r}` as MessageKey)}
                      </button>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="download"
                    disabled={!data.points.length}
                    onClick={() =>
                      downloadCsv(
                        `${d.code}-${range}.csv`,
                        [t('iot.time'), t('iot.average'), t('iot.lowest_seen'), t('iot.highest_seen')],
                        data.points.map((p) => [p.t, p.v / 1000, p.lo / 1000, p.hi / 1000]),
                      )
                    }
                  >
                    {t('iot.csv')}
                  </Button>
                </span>
              </div>
              <Chart d={d} points={data.points} range={range} now={data.now} />
            </section>
            <div className="iot-two">
              <section className="panel">
                <h2 className="iot-h2">{t('iot.alerts_of')}</h2>
                {!data.alerts.length ? (
                  <p className="muted">{t('iot.no_alerts_device')}</p>
                ) : (
                  <ul className="iot-alerts">
                    {data.alerts.map((a) => (
                      <AlertItem key={a.id} a={a} device={d} onAck={can('iot.record') ? () => setAck(a) : undefined} />
                    ))}
                  </ul>
                )}
              </section>
              <section className="panel">
                <h2 className="iot-h2">{t('iot.latest')}</h2>
                {!data.recent.length ? (
                  <p className="muted">{t('iot.no_readings')}</p>
                ) : (
                  <Table
                    head={[t('iot.time'), t('iot.value'), t('iot.from')]}
                    left={[2]}
                    rows={data.recent.slice(0, 10).map((r) => ({
                      key: r.id,
                      cells: [dateTime(r.at), valueText(r.value, d), r.by_name ?? <span className="muted">{t('iot.sensor')}</span>],
                    }))}
                  />
                )}
              </section>
            </div>
          </>
        )}
        {edit && d ? <DeviceDialog device={d} onClose={() => setEdit(false)} onSaved={reload} /> : null}
        {log && d ? <ReadingDialog d={d} onClose={() => setLog(false)} onSaved={reload} /> : null}
        {ack ? <AckDialog a={ack} onClose={() => setAck(null)} onDone={reload} /> : null}
      </div>
    </Shell>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rep-kpi">
      <span className="rep-kpi-label">{label}</span>
      <span className="rep-kpi-value">{value}</span>
      <span className="rep-kpi-foot muted">{hint ?? ' '}</span>
    </div>
  );
}

function Chart({ d, points, range, now }: { d: Device; points: Detail['points']; range: Range; now: string }) {
  const [hover, setHover] = useState<number | null>(null);
  // A narrower drawing on a phone keeps the axis text readable.
  const [narrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches);
  if (points.length < 2) return <p className="muted iot-chart-empty">{t('iot.not_enough')}</p>;
  const W = narrow ? 360 : 800;
  const H = narrow ? 220 : 240;
  const P = { l: 52, r: 12, t: 12, b: 28 };
  const end = Date.parse(now);
  const span = range === '24h' ? 86_400_000 : range === '7d' ? 7 * 86_400_000 : 30 * 86_400_000;
  const start = end - span;
  const vals = [...points.flatMap((p) => [p.lo, p.hi]), ...(d.min !== null ? [d.min] : []), ...(d.max !== null ? [d.max] : [])];
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  const pad = (hi - lo || Math.abs(hi) || 1000) * 0.1;
  lo -= pad;
  hi += pad;
  const x = (ts: number) => P.l + ((ts - start) / span) * (W - P.l - P.r);
  const y = (v: number) => P.t + (1 - (v - lo) / (hi - lo)) * (H - P.t - P.b);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(Date.parse(p.t)).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  const band = `${points.map((p, i) => `${i ? 'L' : 'M'}${x(Date.parse(p.t)).toFixed(1)} ${y(p.hi).toFixed(1)}`).join(' ')} ${[...points]
    .reverse()
    .map((p) => `L${x(Date.parse(p.t)).toFixed(1)} ${y(p.lo).toFixed(1)}`)
    .join(' ')} Z`;
  const ticks = Array.from({ length: 5 }, (_, i) => lo + ((hi - lo) * i) / 4);
  const fmtX = new Intl.DateTimeFormat(locale(), range === '24h' ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: 'short' });
  const nx = narrow ? 3 : 5;
  const xs = Array.from({ length: nx }, (_, i) => start + (span * i) / (nx - 1));
  const h = hover === null ? null : points[hover]!;
  return (
    <figure className="iot-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t('iot.chart_label', { name: d.name })}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const ts = start + (((e.clientX - r.left) / r.width) * W - P.l) / (W - P.l - P.r) * span;
          let best = 0;
          points.forEach((p, i) => {
            if (Math.abs(Date.parse(p.t) - ts) < Math.abs(Date.parse(points[best]!.t) - ts)) best = i;
          });
          setHover(best);
        }}
      >
        {d.min !== null || d.max !== null ? (
          <rect
            className="iot-safe"
            x={P.l}
            width={W - P.l - P.r}
            y={y(d.max ?? hi)}
            height={Math.max(0, y(d.min ?? lo) - y(d.max ?? hi))}
          />
        ) : null}
        {ticks.map((v) => (
          <g key={v}>
            <line className="iot-grid" x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} />
            <text className="iot-axis" x={P.l - 8} y={y(v) + 4} textAnchor="end">
              {valueText(Math.round(v), { ...d, unit: '' })}
            </text>
          </g>
        ))}
        {xs.map((ts, i) => (
          <text key={ts} className="iot-axis" x={x(ts)} y={H - 8} textAnchor={i === 0 ? 'start' : i === xs.length - 1 ? 'end' : 'middle'}>
            {fmtX.format(new Date(ts))}
          </text>
        ))}
        <path className="iot-band" d={band} />
        <path className="iot-path" d={line} />
        {d.max !== null ? <line className="iot-limit" x1={P.l} x2={W - P.r} y1={y(d.max)} y2={y(d.max)} /> : null}
        {d.min !== null ? <line className="iot-limit" x1={P.l} x2={W - P.r} y1={y(d.min)} y2={y(d.min)} /> : null}
        {h ? (
          <g>
            <line className="iot-cursor" x1={x(Date.parse(h.t))} x2={x(Date.parse(h.t))} y1={P.t} y2={H - P.b} />
            <circle className="iot-dot" cx={x(Date.parse(h.t))} cy={y(h.v)} r={4} />
          </g>
        ) : null}
      </svg>
      <figcaption className={`iot-tip${h ? ' on' : ''}`} aria-live="polite">
        {h ? `${dateTime(h.t)} · ${valueText(h.v, d)} (${valueText(h.lo, d)} – ${valueText(h.hi, d)})` : t('iot.chart_hint')}
      </figcaption>
    </figure>
  );
}

function ReadingDialog({ d, onClose, onSaved }: { d: Device; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [text, setText] = useState('');
  const v = text.trim() ? parseValue(text) : null;
  const out = v === null ? null : d.max !== null && v > d.max ? 'high' : d.min !== null && v < d.min ? 'low' : 'ok';
  return (
    <Dialog
      title={t('iot.write_reading_for', { name: d.name })}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={v === null}
            onClick={() =>
              void run(async () => {
                await action('iot.add_reading', { device: d.id, value: v });
                await onSaved();
                onClose();
              }, t('iot.reading_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('iot.value_in', { unit: d.unit || '—' })} hint={rangeText(d)} error={text.trim() && v === null ? t('iot.number_bad') : undefined} required>
          <input className="input iot-big-input" inputMode="decimal" autoFocus value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        {out && out !== 'ok' ? (
          <p className="callout iot-danger">
            <Icon name="alert" size={18} />
            <span>{t(out === 'high' ? 'iot.will_high' : 'iot.will_low')}</span>
          </p>
        ) : null}
        <p className="muted small">{t('iot.reading_note')}</p>
      </div>
    </Dialog>
  );
}
